import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { css } from '@emotion/react';
import { gray6 } from 'styles/global_defaults/colors';
import {
  doubleSpacing,
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import { textMediumLineHeight } from 'styles/global_defaults/fonts';

import { NvTooltip } from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import { doubleModalZIndex } from 'shared/components/nv-modal';
import ParticipantDot from 'athena/components/participant-dot';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';

const styles = css`
  display: grid;
  align-items: center;
  column-gap: ${standardSpacing}px;
  grid-auto-rows: auto;
  grid-template-columns: ${standardSpacing}px auto ${tripleSpacing}px;
  border-bottom: 1px solid ${gray6};
  .switch {
    width: ${doubleSpacing}px;
    height: ${standardSpacing}px;
    transform: scale(1.5);
    justify-self: center;
  }
  .question-text {
    line-height: ${textMediumLineHeight}px !important;
  }
  .mentor-icon, .mentee-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .mentee-icon { grid-row: 1; grid-column: 1; }
  .mentee-text  { grid-row: 1; grid-column: 2; }
  .mentor-icon { grid-row: 2; grid-column: 1; }
  .mentor-text  { grid-row: 2; grid-column: 2; }
  .personalized-switch {
    grid-row: 1 / 3;
    grid-column: 3;
    align-self: center;
  }
`;

const overlayStyles = css`
  max-width: 280px;
  .confirmation {
    max-width: 100%;
    .button-container {
      justify-content: center;
    }
  }
`;

type SelectableQuestionRowProps = {
  isHidden?: boolean;
  isSelected: boolean;
  questionId: number;
  questionText: string;
  onChange: (questionId: number, isSelected: boolean, isAttachedToRule: boolean, attachedRules: { id: number }[]) => void;
  isAttachedToRule: boolean;
  attachedRules?: { id: number }[];
  dataQa?: string;
  dataQaId?: string;
  menteeQuestion?: string;
  mentorQuestion?: string;
  hideMentorResponse?: boolean;
};

const SelectableQuestionRow = ({
  isHidden = false,
  isSelected,
  questionId,
  questionText,
  onChange,
  isAttachedToRule,
  attachedRules,
  dataQa,
  dataQaId,
  menteeQuestion,
  mentorQuestion,
  hideMentorResponse = false,
}: SelectableQuestionRowProps) => {
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const aliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram?.id ?? 0));
  const [isChecked, setIsChecked] = useState(isAttachedToRule || isSelected);
  const [isSwitchingRequired, setIsSwitchingRequired] = useState(false);
  const flyoutTranslationBase = t.MENTORING_PROGRAMS.SETTINGS.TABS.MATCHING_SETTINGS
    .MATCHING_TYPE.CONFIGURATION.ASSIGNED.MATCHING_RULES.FLYOUT_MODAL;
  const tooltip = isHidden ? flyoutTranslationBase.TOOLTIP.HIDDEN() : flyoutTranslationBase.TOOLTIP.SHOWN();
  const isPersonalized = !!(menteeQuestion && mentorQuestion);
  const mentorIconTooltip = hideMentorResponse
    ? flyoutTranslationBase.TOOLTIP.HIDDEN_FOR_MENTEE({ MenteeAlias: aliases.MenteeAlias ?? '' })
    : flyoutTranslationBase.TOOLTIP.SHOWN();

  const handleRequiredChange = (newState) => {
    if (!newState) setIsSwitchingRequired(true);
    else {
      setIsChecked(newState);
      onChange(questionId, newState, isAttachedToRule, attachedRules);
    }
  };

  const handleConfirm = () => {
    setIsSwitchingRequired(false);
    setIsChecked(false);
    onChange(questionId, false, isAttachedToRule, attachedRules);
  };

  return (
    <div css={styles}>
      {isPersonalized ? (
        <>
          <div className='mentee-icon'>
            <NvTooltip placement='top' preventOverflow={false} zIndex={doubleModalZIndex} text={flyoutTranslationBase.TOOLTIP.SHOWN()}>
              <NvIcon size='small' icon='view' />
            </NvTooltip>
          </div>
          <div className='text-large-body semi-bold d-flex align-items-center question-text py-2 mentee-text'>
            <ParticipantDot role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTEE} className='mr-2' />
            {menteeQuestion}
          </div>
          <div className='mentor-icon'>
            <NvTooltip placement='top' preventOverflow={false} zIndex={doubleModalZIndex} text={mentorIconTooltip}>
              <NvIcon size='small' icon={hideMentorResponse ? 'hide' : 'view'} />
            </NvTooltip>
          </div>
          <div className='text-large-body semi-bold d-flex align-items-center question-text py-2 mentor-text'>
            <ParticipantDot role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR} className='mr-2' />
            {mentorQuestion}
          </div>
        </>
      ) : (
        <>
          <NvTooltip placement='top' preventOverflow={false} zIndex={doubleModalZIndex} text={tooltip}>
            <NvIcon size='small' icon={isHidden ? 'hide' : 'view'} />
          </NvTooltip>
          <div className='text-large-body semi-bold d-flex flex-row align-items-center question-text py-2'>
            {questionText}
          </div>
        </>
      )}
      <NvPopover
        enabled
        show={isSwitchingRequired}
        overlayStyles={overlayStyles}
        content={(
          <NvConfirmationPopover
            onCancel={() => setIsSwitchingRequired(false)}
            onConfirm={handleConfirm}
            header={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.TITLE()}
            message={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.DESCRIPTION()}
            cancelBtnText={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.CANCEL()}
            confirmBtnText={t.MENTORING_PROGRAMS.SETTINGS.TABS.PROGRAM_PROFILE_SETUP.PROGRAM_LEVEL_FIELDS.REQUIRED_POPOVER.OK()}
          />
        )}
        placement='left'
        rootClose
        onHide={() => setIsSwitchingRequired(false)}
        className={`d-inline-flex mw-100${isPersonalized ? ' personalized-switch' : ''}`}
        preventOverflow
      >
        <NvSwitch
          checked={isChecked}
          className='switch align-self-center'
          onChange={handleRequiredChange}
          dataQa={dataQa}
          dataQaId={dataQaId}
          ariaLabel={
            isChecked
              ? flyoutTranslationBase.ARIA_LABEL.DISABLE_RULE(questionText)
              : flyoutTranslationBase.ARIA_LABEL.ENABLE_RULE(questionText)
          }
        />
      </NvPopover>
    </div>
  );
};

export default SelectableQuestionRow;
